/// <reference types="vite/client" />
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
   createRootRoute,
   HeadContent,
   Outlet,
   Scripts,
   useRouterState,
} from '@tanstack/react-router'
import { Provider } from 'react-redux'
import { makeStore } from 'store/init-store'
import { AuthProvider } from 'lib/auth/auth-context'
import { SignInGate } from 'lib/auth/sign-in-gate'
import { useFavoritesSync } from 'hooks/useFavoritesSync'
import { usePrefsSync } from 'hooks/usePrefsSync'
import { trackPageView, startAnalytics } from 'lib/analytics/events'
import { getUserSettings, initTheme } from 'services/user.service'

// Global styles. These live here rather than in a client entry so the server
// render emits their <link> tags in the initial HTML.
import 'styles/tailwind.css'
import 'styles/components.css'
import 'styles/third-party.css'
import 'styles/blog.css'

// Runs before first paint, ahead of React, so a dark-mode visitor never sees a
// flash of the light theme: the server has no way to know their preference, so
// the document would otherwise render light and only switch once the app
// hydrated and read localStorage. Mirrors applyTheme() in user.service.ts.
const ThemeScript = `
try {
   var prefs = JSON.parse(localStorage.getItem('t2e:userPrefs') || '{}')
   var theme = prefs.theme || 'auto'
   var dark = theme === 'dark' ||
      (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)
   if (dark) document.documentElement.setAttribute('data-theme', 'dark')
} catch (e) {}
`

// Sends a GA4 page_view for every navigation, including the first paint.
// Waiting for the page's title is the tracker's job (see trackPageView); this
// only has to say that a navigation happened.
//
// Booting analytics lives in this same effect rather than one of its own. As
// two effects it was two orderable statements, and the order was wrong: React
// runs them in declaration order, so trackPageView() went first, before
// startAnalytics() had created window.dataLayer, and every visit's landing
// page_view was dropped. Both calls being inside one effect body is what makes
// that unrepresentable. startAnalytics is idempotent, so running it per
// navigation costs nothing, and going first means the page_view carries this
// visitor's user properties.
const useAnalytics = (): void => {
   const href = useRouterState({ select: (state) => state.location.href })

   useEffect(() => {
      startAnalytics()
      trackPageView()
   }, [href])
}

// Reads localStorage, so it cannot run during SSR. It ran at module scope in
// the old main.tsx; effects only run in the browser.
const useTheme = (): void => {
   useEffect(() => {
      initTheme(getUserSettings().theme)
   }, [])
}

function RootDocument({ children }: { children: ReactNode }) {
   return (
      <html lang="en">
         <head>
            <HeadContent />
            <script dangerouslySetInnerHTML={{ __html: ThemeScript }} />
         </head>
         <body>
            {children}
            <Scripts />
         </body>
      </html>
   )
}

// SignInGate sits here rather than outside the router: the sign-in screen links
// to /site/terms and /site/privacy, and a TanStack <Link> outside RouterProvider
// throws in useLinkProps, whitescreening the whole app the first time a 403
// opened the screen. Inside the root route it is above every page and still
// inside the router.
//
// The Redux store is created per render (see makeStore) so that server renders
// of different requests never share transient UI state.
// Renders nothing; exists only because the hook needs to sit inside both the
// Redux provider and the auth provider, and RootComponent is outside both.
function FavoritesSync() {
   useFavoritesSync()
   return null
}

// Its own component rather than a second hook inside FavoritesSync, so a
// failure in one feature's sync can't take the other's with it.
function PrefsSync() {
   usePrefsSync()
   return null
}

function RootComponent() {
   useAnalytics()
   useTheme()
   const [store] = useState(makeStore)

   return (
      <Provider store={store}>
         <AuthProvider>
            <FavoritesSync />
            <PrefsSync />
            <SignInGate>
               <Outlet />
            </SignInGate>
         </AuthProvider>
      </Provider>
   )
}

export const Route = createRootRoute({
   head: () => ({
      meta: [
         { charSet: 'utf-8' },
         { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
         // Site-wide defaults. A child route's title wins over this one, and
         // its name/property meta tags replace the matching entries here, so
         // this is only what shows on a page that says nothing of its own.
         { title: 'Thai2English' },
         { name: 'theme-color', content: '#ffffff' },
         { name: 'twitter:card', content: 'summary' },
         { name: 'twitter:site', content: '@thai2english' },
         { name: 'twitter:creator', content: '@thai2english' },
         { name: 'build-version', content: __BUILD_VERSION__ },
         { name: 'build-time', content: __BUILD_TIME__ },
      ],
      links: [
         { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
         { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
         { rel: 'manifest', href: '/site.webmanifest' },
      ],
   }),
   shellComponent: RootDocument,
   component: RootComponent,
})
